import { ButtonBase, Typography } from "@mui/material";
import Box from "@mui/material/Box";
import {
    memo,
    useCallback,
    useEffect,
    useState,
} from "react";

import PictureWithBlur from "./picture-with-blur";
import getApiUrl from "@/lib/project-urls";
import { AvatarPlusProps } from "@/types/user";

const AVATAR_COLORS = [
    "#E57373",
    "#F06292",
    "#BA68C8",
    "#9575CD",
    "#7986CB",
    "#64B5F6",
    "#4FC3F7",
    "#4DD0E1",
    "#4DB6AC",
    "#81C784",
    "#AED581",
    "#DCE775",
    "#FFD54F",
    "#FFB74D",
    "#FF8A65",
    "#A1887F",
    "#90A4AE",
];

function getAvatarColor(value: string): string {
    let hash = 0;

    for (let index = 0; index < value.length; index += 1) {
        hash = value.charCodeAt(index) + ((hash << 5) - hash);
    }

    const colorIndex = Math.abs(hash) % AVATAR_COLORS.length;

    return AVATAR_COLORS[colorIndex];
}

function isColorDark(hexColor: string): boolean {
    const red = parseInt(hexColor.slice(1, 3), 16);
    const green = parseInt(hexColor.slice(3, 5), 16);
    const blue = parseInt(hexColor.slice(5, 7), 16);

    const luminance =
        0.299 * red +
        0.587 * green +
        0.114 * blue;

    return luminance < 140;
}

function AvatarPlus({
                        src,
                        size = 50,
                        sx,
                        name,
                        openByClick,
                        alt,
                        ...others
                    }: AvatarPlusProps) {
    const [imageHasError, setImageHasError] = useState(false);

    const numericSize =
        typeof size === "number"
            ? size
            : Number(size) || 50;

    const normalizedName =
        typeof name === "string" && name.trim()
            ? name.trim()
            : "ناشناس";

    const backgroundColor = getAvatarColor(normalizedName);

    const textColor = isColorDark(backgroundColor)
        ? "common.white"
        : "common.black";

    const initials =
        normalizedName === "ناشناس"
            ? "؟"
            : normalizedName
                .split(/\s+/)
                .filter(Boolean)
                .map((word) => word[0])
                .slice(0, 2)
                .join("‌");

    useEffect(() => {
        setImageHasError(false);
    }, [src]);

    const handleImageError = useCallback(() => {
        setImageHasError(true);
    }, []);

    const hasValidImage = Boolean(src) && !imageHasError;

    const content = hasValidImage ? (
        <PictureWithBlur
            {...others}
            key={src}
            src={src}
            alt={alt || normalizedName}
            onError={handleImageError}
            width={numericSize}
            height={numericSize}
        />
    ) : (
        <Typography
            component="span"
            fontSize={numericSize * 0.4}
            fontWeight={400}
            color={textColor}
            textAlign="center"
            sx={{
                userSelect: "none",
                mb:.5
            }}
        >
            {initials}
        </Typography>
    );

    return (
        <Box
            display="flex"
            justifyContent="center"
            alignItems="center"
            bgcolor={backgroundColor}
            width={size}
            height={size}
            borderRadius="50%"
            overflow="hidden"
            flexShrink={0}
            sx={sx}
        >
            {openByClick && src && !imageHasError ? (
                <ButtonBase
                    component="a"
                    href={getApiUrl(src) ?? "#"}
                    onClick={(event) => event.stopPropagation()}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label={`مشاهده تصویر ${normalizedName}`}
                    sx={{
                        width: "100%",
                        height: "100%",
                        borderRadius: "50%",
                    }}
                >
                    {content}
                </ButtonBase>
            ) : (
                content
            )}
        </Box>
    );
}

export default memo(AvatarPlus, (prevProps, nextProps) => {
    return (
        prevProps.src === nextProps.src &&
        prevProps.name === nextProps.name &&
        prevProps.size === nextProps.size &&
        prevProps.openByClick === nextProps.openByClick &&
        prevProps.alt === nextProps.alt &&
        prevProps.sx === nextProps.sx
    );
});